import { useCallback, useMemo, useState } from "react"
import { Box, Grid } from "@mui/material"
import { makeStyles } from "tss-react/mui"
import { CreateUpdateBooking, ResourceFiltersTypes } from "../../createOrderTypes"
import { WeeklyResourceBookingCalendar } from "./resourceBookingCalendars/WeeklyResourceBookingCalendar"
import { TimelineCalendarResource } from "./calendar/fullCalendarTypes"
import { ResourceBookingCalendar } from "./resourceBookingCalendars/ResourceBookingCalendar"
import { BookingViewMenu, ViewType } from "./resourceBookingCalendars/BookingViewMenu"
import { ResourceFilters } from "./resourceFilters/ResourceFilters"
import { ResourceBookingControls } from "./ResourceBookingControls"
import { useApiQuery } from "../../../../../core/api/useApiQuery"
import { filterResources } from "./helpers"
import {
    ConfigQuery,
    CreateUpdateBookingResponse,
    fetchTimelineResources,
    TimelineResourcesResponse,
} from "../../createOrderApi"
import { OrderFormStatus } from "../../mainForm/helpers"
import { MeetingServiceOrder } from "../../../../../modules/merchant/accounting/accounting/accountingTypes"
import { ResourceBookingConfirmDialog } from "./ResourceBookingConfirmDialog"
import { useResourceBookingDraft } from "./ResourceBookingDraftContext"
import { useConfirmBookingDialog } from "./ResourceBookingConfirmDialogContext"
import { TimelineContextProvider } from "./context/TimelineContextProvider"
import { ResourceDetailsProvider } from "./context/ResourceDetailsProvider"
import { ResourceLabelSharedProvider } from "./context/ResourceLabelContext"
import { useCreateOrderForm, useCreateOrderFormSetOrder } from "../../mainForm/CreateOrderFormContext"
import { ResourceBookingCalendarSkeleton } from "./ResourceBookingCalendarSkeleton"

const useStyles = makeStyles()((theme) => ({
    calendarWrapper: {
        position: "relative",
        flexGrow: 1,
        display: "flex",
        flexDirection: "column",
        height: "100%",
    },
    headerWrap: {
        padding: ".75rem 1rem",
        borderBottom: `1px solid ${theme.palette.tailwind.slate[300]}`,
    },
    button: {
        color: theme.palette.tailwind.slate[800],
        padding: ".5rem 1rem",
        flex: "none",
    },
}))

const transformData = (data: TimelineResourcesResponse) => {
    const serviceCentersData = data?.serviceCenters

    if (!serviceCentersData) {
        return { calendarResources: [], groupImagesMap: new Map<string, string>() }
    }

    const groupImagesMap = new Map<string, string>()
    const calendarResources: TimelineCalendarResource[] = []

    serviceCentersData.forEach((group) => {
        if (group.floorPlan?.imageUrl) {
            groupImagesMap.set(group.name, group.floorPlan.imageUrl)
        }

        group.resources.forEach((resource) => {
            calendarResources.push({
                id: String(resource.id),
                uid: resource.uid,
                location: group.name,
                locationId: `${String(group.id)}_${String(resource.id)}`,
                serviceCenterUid: group.uid,
                title: resource.displayName || resource.name,
                seats: resource.room?.seats,
                floor: resource.room?.floorDetails?.value,
                avEquipments: resource.room?.avEquipments || [],
                buffer: resource.buffer,
                tableSetups: resource.room?.tableSetups,
                outOfService: resource.outOfService,
                image: resource.image,
                meetingUsage: resource.room?.meetingUsage,
                // hasMessagingEnabled: !!group.messaging,
                type: resource.type,
                accessControl: resource.accessControl,
            })
        })
    })

    return { calendarResources, groupImagesMap }
}

type ResourceBookingProps = Readonly<{
    handleOpenSuccessDialog: (orderDetails: CreateUpdateBookingResponse, order: CreateUpdateBooking) => void
    handleClearResourceIds: () => void
    orderDetails?: MeetingServiceOrder
    orderFormStatusRef: React.MutableRefObject<OrderFormStatus>
}>

export function ResourceBooking({
    handleOpenSuccessDialog,
    handleClearResourceIds,
    orderDetails,
    orderFormStatusRef,
}: ResourceBookingProps) {
    const { user, orderContext, setOrderContext, flowConfig } = useCreateOrderForm()
    const { switchToOrder } = useCreateOrderFormSetOrder()
    const { confirmBookingDialog, setConfirmBookingDialog } = useConfirmBookingDialog()
    const { orderId, mode } = orderContext
    const isOutlookFlow = !!flowConfig?.flow

    const { classes } = useStyles()
    const {
        draftEvent: event,
        setDraftEvent,
        syncDraftToFormik,
        initialEventStateId,
        handleCancelClick,
    } = useResourceBookingDraft()

    const [selectedView, setSelectedView] = useState(ViewType.TIMELINE)
    const [filters, setFilters] = useState<ResourceFiltersTypes>({
        filters: [
            {
                identifier: "resourceTypeIds",
                values: [
                    { key: "Room", value: 1 },
                    { key: "Av equipment", value: 2 },
                ],
            },
        ],
        search: "",
        isBookedPeriod: false,
    })

    const searchRegex = useMemo(
        () => (filters.search ? new RegExp(`(${filters.search})`, "i") : undefined),
        [filters.search],
    )

    const {
        data: { calendarResources, groupImagesMap } = {
            calendarResources: [],
            groupImagesMap: new Map<string, string>(),
        },
        isLoading,
    } = useApiQuery(fetchTimelineResources, {
        queryName: ConfigQuery.TIMELINE_RESOURCES_FOR_USER,
        dependencies: {
            userId: user.userId,
            resourceTypes: filters.filters
                .find((f) => f.identifier === "resourceTypeIds")
                ?.values.map((v) => String(v.key).trim().split(" ")[0].toUpperCase())
                .join(","),
        },

        select: transformData,
    })

    const activeResourceIdsKey = selectedView === ViewType.TIMELINE ? (event?.resourceIds?.join("|") ?? "") : ""
    const activeResourceIdsForFiltering = useMemo<readonly string[] | null>(
        () => (activeResourceIdsKey ? activeResourceIdsKey.split("|") : null),
        [activeResourceIdsKey],
    )

    const filteredResources = useMemo(
        () => filterResources(calendarResources, filters.filters, searchRegex, activeResourceIdsForFiltering),
        [calendarResources, filters.filters, searchRegex, activeResourceIdsForFiltering],
    )

    const allServiceCenterIdsForEvents = useMemo(
        () => [...new Set(calendarResources.map((r) => r.locationId.split("_")[0]))],
        [calendarResources],
    )

    const handleBookClick = useCallback(() => {
        syncDraftToFormik()
        setConfirmBookingDialog(true)
    }, [syncDraftToFormik])

    const handleOpenBookDialog = useCallback(() => {
        setConfirmBookingDialog(true)
    }, [])

    const handleChangeBookingView = (view: ViewType) => {
        if (selectedView === ViewType.AVAILABLE_TIME && event) {
            setDraftEvent({ ...event, timeRange: undefined })
        }
        setSelectedView(view)
        handleCancelClick()
    }

    const handleChangeOrderContext = useCallback((id: string) => switchToOrder(+id), [switchToOrder])

    return (
        <Box className={classes.calendarWrapper}>
            <Grid
                container
                justifyContent="space-between"
                flexWrap="nowrap"
                alignItems="center"
                className={classes.headerWrap}
            >
                <ResourceFilters
                    filters={filters}
                    userId={user.userId}
                    setFilters={setFilters}
                    selectedView={selectedView}
                    isTimeline
                    isSpecialFlow={isOutlookFlow}
                />

                <BookingViewMenu selectedView={selectedView} onChangeView={handleChangeBookingView} />
            </Grid>

            {!isLoading ? (
                <ResourceDetailsProvider>
                    {selectedView === ViewType.TIMELINE && (
                        <TimelineContextProvider
                            timelineResources={filteredResources}
                            allServiceCenterIdsForEvents={allServiceCenterIdsForEvents}
                            userId={user.userId}
                            localEventUpdateId={initialEventStateId || orderId}
                            isBookPeriodMode={!!filters.isBookedPeriod}
                            isOutlookFlow={isOutlookFlow}
                            searchRegex={searchRegex}
                        >
                            <ResourceLabelSharedProvider>
                                <ResourceBookingCalendar
                                    groupImagesMap={groupImagesMap}
                                    handleChangeOrderContext={handleChangeOrderContext}
                                />
                            </ResourceLabelSharedProvider>
                        </TimelineContextProvider>
                    )}
                    {selectedView === ViewType.AVAILABLE_TIME && (
                        <WeeklyResourceBookingCalendar
                            timelineResources={filteredResources}
                            searchRegex={searchRegex}
                            groupImagesMap={groupImagesMap}
                            handleBookClick={handleOpenBookDialog}
                        />
                    )}
                </ResourceDetailsProvider>
            ) : (
                <ResourceBookingCalendarSkeleton viewType={selectedView} />
            )}
            {selectedView === ViewType.TIMELINE && (
                <ResourceBookingControls
                    onBook={handleBookClick}
                    isLoadingButton={confirmBookingDialog}
                    flow={flowConfig?.flow}
                    orderFormStatusRef={orderFormStatusRef}
                />
            )}

            {confirmBookingDialog && event && (
                <ResourceBookingConfirmDialog
                    open={confirmBookingDialog}
                    user={user}
                    orderId={orderId}
                    initialEventStateId={initialEventStateId}
                    orderFormStatusRef={orderFormStatusRef}
                    orderDetails={orderDetails}
                    mode={mode}
                    setOrderContext={setOrderContext}
                    handleOpenSuccessDialog={handleOpenSuccessDialog}
                    handleClearResourceIds={handleClearResourceIds}
                />
            )}
        </Box>
    )
}
